/**
 * 출력시트 데이터 추출 유틸리티
 * AHSelInlinePreview.tsx와 동일한 로직으로 데이터를 계산하여 추출
 * 엑셀 export에서 사용
 */

import { PsychrometricCalculator } from './psychrometric';

export interface ProjectInfoData {
  projectName: string;
  location: string;
  engineer: string;
  date: string;
}

export interface AirConditioningSystemData {
  ahuNo: string;
  zoneName: string;
  systemType: string;
  personnel: number;
  area: number;
  ceilingHeight: number;
  volume: number;
  supplyAirflow: number;
  outdoorAirflow: number;
  returnAirflow: number;
  exhaustAirflow: number;
  supplyAirPerArea: string;
  airChange: string;
  sensibleCoolingLoad: number;
  latentCoolingLoad: number;
  totalRoomLoad: number;
  rshf: number;
  deviceCoolingLoad: number;
  deviceHeatingLoad: number;
  deviceCoolingPerArea: string;
  deviceHeatingPerArea: string;
}

export interface DesignConditionsData {
  cooling: {
    outdoor: {
      temp: number;
      humidity: number;
      humidityRatio: number;
      enthalpy: number;
    };
    indoor: {
      temp: number;
      humidity: number;
      humidityRatio: number;
      enthalpy: number;
    };
  };
  heating: {
    outdoor: {
      temp: number;
      humidity: number;
      humidityRatio: number;
      enthalpy: number;
    };
    indoor: {
      temp: number;
      humidity: number;
      humidityRatio: number;
      enthalpy: number;
    };
  };
}

export interface LoadPercentageData {
  cooling: {
    roomLoad: string;
    oaLoad: string;
    addFan: string;
  };
  heating: {
    roomLoad: string;
    oaLoad: string;
    addHum: string;
  };
}

export interface CoilCapacityData {
  cooling: {
    capacity: number;
    formula: string;
  };
  preheating: {
    capacity: number;
    formula: string;
  };
  heating: {
    capacity: number;
    formula: string;
  };
  reheating: {
    capacity: number;
    formula: string;
  };
  humidifier: {
    capacity: number;
    formula: string;
  };
}

export interface PsychrometricPoint {
  name: string;
  displayName?: string;
  dryBulbTemp: number;
  relativeHumidity: number;
  humidityRatio?: number;
  enthalpy?: number;
}

export interface PsychrometricChartData {
  points: PsychrometricPoint[];
  lines: any[];
}

export interface OutputSheetData {
  projectInfo: ProjectInfoData;
  systemInfo: AirConditioningSystemData;
  designConditions: DesignConditionsData;
  loadPercentages: LoadPercentageData;
  coilCapacities: CoilCapacityData;
  psychrometricChart: PsychrometricChartData;
}

/**
 * 프로젝트 정보 추출
 */
export function extractProjectInfo(
  projectName: string,
  currentProject: any
): ProjectInfoData {
  return {
    projectName: projectName || '프로젝트',
    location: currentProject?.location || '',
    engineer: currentProject?.engineer || '',
    date: currentProject?.date || new Date().toISOString().split('T')[0]
  };
}

/**
 * 공조 시스템 정보 추출
 */
export function extractAirConditioningSystemData(
  ahu: any,
  currentProjectAHUs: any[]
): AirConditioningSystemData {
  const currentAHUData = currentProjectAHUs?.find(projectAhu =>
    projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
  ) || {};

  const formData = currentAHUData.formData || ahu.formData || {};
  const calc = currentAHUData.calculationResults || ahu.calculationResults || {};

  // 기본 정보
  const personnel = parseInt(formData.personnel || formData.occupantCount) || 0;
  const area = parseFloat(formData.conditionedArea || formData.airConditioningArea || formData.area) || 0;
  const ceilingHeight = parseFloat(formData.ceilingHeight) || 0;
  const volume = area && ceilingHeight ? Math.round(area * ceilingHeight) : 0;

  // 풍량 정보
  const supplyAirflow = parseFloat(formData.directSupplyAirflow) ||
                       parseFloat(formData.supplyAirflow) ||
                       calc.supplyAirflow || 0;
  const outdoorAirflow = parseFloat(formData.outdoorAirflow) || calc.outdoorAirflow || 0;
  const returnAirflow = parseFloat(formData.returnAirflow) || calc.returnAirflow || 0;
  const exhaustAirflowVal = parseFloat(formData.exhaustAirflow) || 0;
  const pressurizationAirflow = parseFloat(formData.pressurizationAirflow) || 0;
  const exhaustAirflow = exhaustAirflowVal + pressurizationAirflow;

  // 부하 정보
  const sensibleCoolingLoad = parseFloat(formData.sensibleCoolingLoad) || 0;
  const latentCoolingLoad = parseFloat(formData.latentCoolingLoad) || 0;
  const totalRoomLoad = sensibleCoolingLoad + latentCoolingLoad;
  const rshf = totalRoomLoad > 0 ? sensibleCoolingLoad / totalRoomLoad : 0;

  // 장치 부하 계산 (AHSelInlinePreview.tsx의 calculateDeviceLoads 로직과 동일)
  let deviceCoolingLoad = 0;
  let deviceHeatingLoad = 0;

  // 냉방 부하 계산
  if (calc.coolingCapacity) {
    deviceCoolingLoad = calc.coolingCapacity;
  } else if (ahu.coolingCapacity) {
    deviceCoolingLoad = ahu.coolingCapacity;
  } else {
    // 습공기 선도에서 계산
    const psychroPoints = ahu.psychroChartData?.points || currentAHUData.psychroChartData?.points || [];
    const mixedAirPoint = psychroPoints.find((p: any) => p.name === 'ma');
    const supplyAirPoint = psychroPoints.find((p: any) => p.name === 'l1');

    if (mixedAirPoint?.enthalpy && supplyAirPoint?.enthalpy) {
      const enthalpyDiff = mixedAirPoint.enthalpy - supplyAirPoint.enthalpy;
      if (enthalpyDiff > 0 && enthalpyDiff >= 15 && enthalpyDiff <= 35) {
        const coolingSafetyFactor = parseFloat(formData.coolingSafetyFactor || formData.safetyFactor) || 18.7;
        const calculatedCoolingCapacity = supplyAirflow * 0.334 * enthalpyDiff * (1 + coolingSafetyFactor / 100);
        if (calculatedCoolingCapacity > 0 && calculatedCoolingCapacity < 500000) {
          deviceCoolingLoad = calculatedCoolingCapacity;
        }
      }
    }
  }

  // 난방 부하 계산
  const psychroPoints = ahu.psychroChartData?.points || currentAHUData.psychroChartData?.points || [];
  const mixedAirPointHeating = psychroPoints.find((p: any) => p.name === 'm1');
  const heatingSupplyAirPoint = psychroPoints.find((p: any) => p.name === 'l2');

  if (mixedAirPointHeating?.dryBulbTemp !== undefined && heatingSupplyAirPoint?.dryBulbTemp !== undefined) {
    const mixedAirTemp = mixedAirPointHeating.dryBulbTemp;
    const supplyAirTemp = heatingSupplyAirPoint.dryBulbTemp;
    const tempDiff = supplyAirTemp - mixedAirTemp;

    if (tempDiff > 0 && tempDiff <= 50) {
      const heatingSafetyFactor = parseFloat(formData.heatingSafetyFactor || formData.safetyFactor) || 10.0;
      const displaySupplyAirflow = Math.round(supplyAirflow * 10) / 10;
      const displaySupplyTemp = Math.round(supplyAirTemp * 10) / 10;
      const displayMixedTemp = Math.round(mixedAirTemp * 10) / 10;
      const displayTempDiff = Math.round((displaySupplyTemp - displayMixedTemp) * 10) / 10;
      const displaySafetyFactor = Math.round((1 + heatingSafetyFactor / 100) * 100) / 100;

      const calculatedHeatingCapacity = Math.round(0.335 * displaySupplyAirflow * displayTempDiff * displaySafetyFactor);
      if (calculatedHeatingCapacity > 0 && calculatedHeatingCapacity < 500000) {
        deviceHeatingLoad = calculatedHeatingCapacity;
      }
    }
  }

  // 계산 실패 시 폴백
  if (deviceHeatingLoad === 0) {
    deviceHeatingLoad = parseFloat(formData.heatingLoad) || 0;
  }

  // 단위 면적/환기 횟수
  const supplyAirPerArea = area > 0 ? (supplyAirflow / area).toFixed(1) : '0';
  const airChange = volume > 0 ? (supplyAirflow / volume).toFixed(2) : '0';
  const deviceCoolingPerArea = area > 0 ? (deviceCoolingLoad / area).toFixed(1) : '0';
  const deviceHeatingPerArea = area > 0 ? (deviceHeatingLoad / area).toFixed(1) : '0';

  return {
    ahuNo: ahu.ahuNo || ahu.id || '',
    zoneName: formData.zoneName || '',
    systemType: formData.systemType || '',
    personnel,
    area,
    ceilingHeight,
    volume,
    supplyAirflow,
    outdoorAirflow,
    returnAirflow,
    exhaustAirflow,
    supplyAirPerArea,
    airChange,
    sensibleCoolingLoad,
    latentCoolingLoad,
    totalRoomLoad,
    rshf,
    deviceCoolingLoad,
    deviceHeatingLoad,
    deviceCoolingPerArea,
    deviceHeatingPerArea
  };
}

/**
 * 외기/실내 설계 조건 추출
 * AHSelInlinePreview.tsx의 라인 746-799와 동일한 로직
 */
export function extractDesignConditions(
  ahu: any,
  currentProjectAHUs: any[]
): DesignConditionsData {
  const currentAHUData = currentProjectAHUs?.find(projectAhu =>
    projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
  ) || {};

  const formData = currentAHUData.formData || ahu.formData || {};
  const ahuCalculationResults = currentAHUData.calculationResults || ahu.calculationResults || {};

  // 온도/습도 값
  const outdoorCoolingTemp = parseFloat(formData.outdoorCoolingTemp) ||
                            ahuCalculationResults?.outdoorAir?.dryBulbTemp || 31.2;
  const outdoorCoolingHumidity = parseFloat(formData.outdoorCoolingHumidity) ||
                                ahuCalculationResults?.outdoorAir?.relativeHumidity || 69.0;
  const indoorCoolingTemp = parseFloat(formData.coolingRoomTemp) ||
                           ahuCalculationResults?.returnAir?.dryBulbTemp || 26.0;
  const indoorCoolingHumidity = parseFloat(formData.coolingRoomHumidity) ||
                               ahuCalculationResults?.returnAir?.relativeHumidity || 55.0;

  const outdoorHeatingTemp = parseFloat(formData.outdoorHeatingTemp) || -11.9;
  const outdoorHeatingHumidity = parseFloat(formData.outdoorHeatingHumidity) || 69.0;
  const indoorHeatingTemp = parseFloat(formData.heatingRoomTemp) || 20.0;
  const indoorHeatingHumidity = parseFloat(formData.heatingRoomHumidity) || 40.0;

  const elevation = parseFloat(formData.elevation) || 0;

  // VBA 방식으로 절대습도와 엔탈피 계산
  // 1. 건구온도 + 상대습도 → 절대습도 계산
  // 2. 절대습도를 vbaRound(4자리)
  // 3. 건구온도 + vbaRound(절대습도) → 엔탈피 계산

  // 냉방 외기 조건
  const outdoorCoolingHR = PsychrometricCalculator.calculateHumidityRatio(
    outdoorCoolingTemp, outdoorCoolingHumidity, elevation
  );
  const outdoorCoolingHRRounded = PsychrometricCalculator.vbaRound(outdoorCoolingHR, 4);
  const outdoorCoolingEnthalpy = PsychrometricCalculator.calculateEnthalpy(
    outdoorCoolingTemp, outdoorCoolingHRRounded
  );

  // 냉방 실내 조건
  const indoorCoolingHR = PsychrometricCalculator.calculateHumidityRatio(
    indoorCoolingTemp, indoorCoolingHumidity, elevation
  );
  const indoorCoolingHRRounded = PsychrometricCalculator.vbaRound(indoorCoolingHR, 4);
  const indoorCoolingEnthalpy = PsychrometricCalculator.calculateEnthalpy(
    indoorCoolingTemp, indoorCoolingHRRounded
  );

  // 난방 외기 조건
  const outdoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(
    outdoorHeatingTemp, outdoorHeatingHumidity, elevation
  );
  const outdoorHeatingHRRounded = PsychrometricCalculator.vbaRound(outdoorHeatingHR, 4);
  const outdoorHeatingEnthalpy = PsychrometricCalculator.calculateEnthalpy(
    outdoorHeatingTemp, outdoorHeatingHRRounded
  );

  // 난방 실내 조건
  const indoorHeatingHR = PsychrometricCalculator.calculateHumidityRatio(
    indoorHeatingTemp, indoorHeatingHumidity, elevation
  );
  const indoorHeatingHRRounded = PsychrometricCalculator.vbaRound(indoorHeatingHR, 4);
  const indoorHeatingEnthalpy = PsychrometricCalculator.calculateEnthalpy(
    indoorHeatingTemp, indoorHeatingHRRounded
  );

  return {
    cooling: {
      outdoor: {
        temp: outdoorCoolingTemp,
        humidity: outdoorCoolingHumidity,
        humidityRatio: outdoorCoolingHRRounded,
        enthalpy: outdoorCoolingEnthalpy
      },
      indoor: {
        temp: indoorCoolingTemp,
        humidity: indoorCoolingHumidity,
        humidityRatio: indoorCoolingHRRounded,
        enthalpy: indoorCoolingEnthalpy
      }
    },
    heating: {
      outdoor: {
        temp: outdoorHeatingTemp,
        humidity: outdoorHeatingHumidity,
        humidityRatio: outdoorHeatingHRRounded,
        enthalpy: outdoorHeatingEnthalpy
      },
      indoor: {
        temp: indoorHeatingTemp,
        humidity: indoorHeatingHumidity,
        humidityRatio: indoorHeatingHRRounded,
        enthalpy: indoorHeatingEnthalpy
      }
    }
  };
}

/**
 * 부하 비율(%) 추출
 * AHSelInlinePreview.tsx의 라인 534-604와 동일한 로직
 */
export function extractLoadPercentages(
  ahu: any,
  currentProjectAHUs: any[],
  systemInfo: AirConditioningSystemData
): LoadPercentageData {
  const currentAHUData = currentProjectAHUs?.find(projectAhu =>
    projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
  ) || {};

  const formData = currentAHUData.formData || ahu.formData || {};
  const calc = currentAHUData.calculationResults || ahu.calculationResults || {};

  const { sensibleCoolingLoad, latentCoolingLoad, deviceCoolingLoad, deviceHeatingLoad, supplyAirflow } = systemInfo;
  const ventilationLightingLoad = parseFloat(formData.ventilationLightingLoad) || 0;

  // 냉방 부하(%)
  let coolingRoomLoad = '';
  let coolingOALoad = '';
  let coolingAddFan = '';

  if (deviceCoolingLoad > 0) {
    // RA Lighting Load 적용 확인
    const isRALightingIgnored = formData.raLightingLoadIgnored === true ||
                              formData.raLightingLoadApplication === 'ignore' ||
                              formData.raLightingLoadApplication === '무시';

    // Room Load 계산
    // VBA: Application.Round(..., 2)
    let roomLoadValue: number;
    if (isRALightingIgnored) {
      // RA Lighting Load가 무시인 경우: (냉방현열부하 + 냉방잠열부하) / 장치냉방부하 * 100
      roomLoadValue = PsychrometricCalculator.vbaRound((sensibleCoolingLoad + latentCoolingLoad) / deviceCoolingLoad * 100, 2);
    } else {
      // 그 밖의 경우: (냉방현열부하 + 냉방잠열부하 + 환기조명부하) / 장치냉방부하 * 100
      roomLoadValue = PsychrometricCalculator.vbaRound((sensibleCoolingLoad + latentCoolingLoad + ventilationLightingLoad) / deviceCoolingLoad * 100, 2);
    }
    coolingRoomLoad = roomLoadValue.toString();

    // OA Load 계산: 냉방외기부하 / 장치냉방부하 * 100
    // VBA: Application.Round(ah1.Cells(Re, 107) / ah1.Cells(Re, 102) * 100, 2)

    // localStorage에서 최신 계산 결과 가져오기
    let outdoorCoolingLoadFromStorage = 0;
    try {
      const calcResults = JSON.parse(localStorage.getItem('calculationResults') || '{}');
      outdoorCoolingLoadFromStorage = calcResults[currentAHUData.id || ahu.id] || 0;
    } catch (e) {
      // localStorage 읽기 실패
    }

    const outdoorCoolingLoad = parseFloat(formData.outdoorCoolingLoad) ||
                              outdoorCoolingLoadFromStorage ||
                              calc.outdoorCoolingLoad ||
                              currentAHUData.outdoorCoolingLoad || 0;
    // console.log('🔍 [OA Load 계산]', {
    //   outdoorCoolingLoad,
    //   deviceCoolingLoad,
    //   '나누기 결과': outdoorCoolingLoad / deviceCoolingLoad,
    //   '곱하기 100': outdoorCoolingLoad / deviceCoolingLoad * 100,
    //   'vbaRound 전': outdoorCoolingLoad / deviceCoolingLoad * 100
    // });
    const oaLoadValue = PsychrometricCalculator.vbaRound(outdoorCoolingLoad / deviceCoolingLoad * 100, 2);
    // console.log('🔍 [OA Load 결과]', { oaLoadValue, '문자열': oaLoadValue.toString() });
    coolingOALoad = oaLoadValue.toString();

    // Add & Hum 계산: 100 - Room Load - OA Load
    // VBA: .Cells(P1 + 9, 14) = 100 - .Cells(P1 + 7, 14) - .Cells(P1 + 8, 14)
    const addFanValue = 100 - roomLoadValue - oaLoadValue;
    coolingAddFan = addFanValue.toString();
  }

  // 난방 부하(%) 계산 - 예열코일과 가열코일 고려
  let heatingRoomLoad = '';
  let heatingOALoad = '';
  let heatingAddHum = '';

  // 예열코일 용량 계산
  let preheatingCoilCapacity = 0;
  const preheatingCoilType = formData.preheatingCoilType || 'None';
  if (preheatingCoilType !== 'None') {
    const phPreheatingTemp = parseFloat(formData.phPreheatingTemp) || 0;
    const phSafetyFactor = parseFloat(formData.preheatingCoilSafetyFactor) || 10.0;

    if (phPreheatingTemp > 0) {
      const psychroPoints = ahu.psychroChartData?.points || currentAHUData.psychroChartData?.points || [];
      const mixedAirPoint = psychroPoints.find((p: any) => p.name === 'm1');
      if (mixedAirPoint?.dryBulbTemp !== undefined) {
        const mixedAirTemp = mixedAirPoint.dryBulbTemp;
        const phTempDiff = phPreheatingTemp - mixedAirTemp;
        preheatingCoilCapacity = PsychrometricCalculator.vbaRoundUp(
          0.335 * supplyAirflow * phTempDiff * (1 + phSafetyFactor / 100),
          0
        );
      }
    }
  }

  // 장치난방부하 = 예열코일 용량 + 가열코일 용량
  const totalHeatingCapacity = preheatingCoilCapacity + deviceHeatingLoad;

  if (totalHeatingCapacity > 0) {
    const heatingRoomLoadValue = parseFloat(formData.heatingLoad) || 0;
    const outdoorHeatingLoad = calc.outdoorHeatingLoad || 0;

    // VBA: Application.Round(Hth / (ah1.Cells(Re, 104) + ah1.Cells(Re, 105)) * 100, 2)
    const roomLoadNum = PsychrometricCalculator.vbaRound(heatingRoomLoadValue / totalHeatingCapacity * 100, 2);
    heatingRoomLoad = roomLoadNum.toString();

    // VBA: Application.Round(ah1.Cells(Re, 108) / (ah1.Cells(Re, 104) + ah1.Cells(Re, 105)) * 100, 2)
    const oaLoadNum = PsychrometricCalculator.vbaRound(outdoorHeatingLoad / totalHeatingCapacity * 100, 2);
    heatingOALoad = oaLoadNum.toString();

    // VBA: .Cells(P1 + 12, 14) = 100 - .Cells(P1 + 10, 14) - .Cells(P1 + 11, 14)
    const addHumValue = 100 - roomLoadNum - oaLoadNum;
    heatingAddHum = addHumValue.toString();
  }

  return {
    cooling: {
      roomLoad: coolingRoomLoad,
      oaLoad: coolingOALoad,
      addFan: coolingAddFan
    },
    heating: {
      roomLoad: heatingRoomLoad,
      oaLoad: heatingOALoad,
      addHum: heatingAddHum
    }
  };
}

/**
 * 코일 용량 데이터 추출
 * AHSelInlinePreview.tsx의 라인 1140-1450과 동일한 로직
 */
export function extractCoilCapacities(
  ahu: any,
  currentProjectAHUs: any[],
  systemInfo: AirConditioningSystemData,
  designConditions: DesignConditionsData
): CoilCapacityData {
  const currentAHUData = currentProjectAHUs?.find(projectAhu =>
    projectAhu.ahuNo === ahu.ahuNo || projectAhu.id === ahu.id
  ) || {};

  const formData = currentAHUData.formData || ahu.formData || {};
  const calc = currentAHUData.calculationResults || ahu.calculationResults || {};
  const psychroPoints = ahu.psychroChartData?.points || [];

  const { supplyAirflow, outdoorAirflow, deviceCoolingLoad, deviceHeatingLoad } = systemInfo;

  // 냉방 코일
  const mixedAirPoint = psychroPoints.find((p: any) => p.name === 'ma');
  const supplyAirPoint = psychroPoints.find((p: any) => p.name === 'l1');

  let coolingCapacity = deviceCoolingLoad;
  let coolingFormula = '';

  if (mixedAirPoint?.enthalpy && supplyAirPoint?.enthalpy) {
    const enthalpyDiff = mixedAirPoint.enthalpy - supplyAirPoint.enthalpy;
    const coolingSafetyFactor = parseFloat(formData.coolingSafetyFactor) || 10.0;
    const coolingFormula1 = `${supplyAirflow.toFixed(0)} ㎥/h × (${mixedAirPoint.enthalpy.toFixed(2)} - ${supplyAirPoint.enthalpy.toFixed(2)}) kJ/kg / 60`;
    const coolingFormula2 = `× (1 + ${coolingSafetyFactor.toFixed(1)} / 100)`;
    coolingFormula = `${coolingFormula1} ${coolingFormula2} = ${coolingCapacity.toFixed(0)} kW`;
  }

  // 예열 코일
  let preheatingCapacity = 0;
  let preheatingFormula = '';

  const preheatingCoilType = formData.preheatingCoilType || 'None';
  if (preheatingCoilType !== 'None') {
    const phPreheatingTemp = parseFloat(formData.preheatingOutletTemp) || 10.0;
    const phSafetyFactor = parseFloat(formData.preheatingSafetyFactor) || 10.0;
    const phTempDiff = phPreheatingTemp - designConditions.heating.outdoor.temp;

    if (phTempDiff > 0) {
      preheatingCapacity = PsychrometricCalculator.vbaRoundUp(
        0.335 * supplyAirflow * phTempDiff * (1 + phSafetyFactor / 100),
        0
      );
      preheatingFormula = `0.335 × ${supplyAirflow.toFixed(0)} ㎥/h × ${phTempDiff.toFixed(1)} ℃ × (1 + ${phSafetyFactor.toFixed(1)} / 100) = ${preheatingCapacity.toFixed(0)} W`;
    }
  }

  // 재열 코일
  let reheatingCapacity = 0;
  let reheatingFormula = '';

  const reheatingCoilType = formData.reheatingCoilType || 'None';
  if (reheatingCoilType !== 'None') {
    const rhTempDiff = parseFloat(formData.rhTempDiff) || 0;
    const rhSafetyFactor = parseFloat(formData.rhSafetyFactor) || 10.0;

    if (reheatingCoilType === 'Steam') {
      const supplyAirTemp = parseFloat(formData.coolingSupplyTemp) || 16.0;
      const reheatingHeatCapacity = 1.006 + 1.86 * (designConditions.cooling.indoor.humidityRatio || 0);
      const airflowKgS = supplyAirflow * 1.2 / 3600;

      reheatingCapacity = PsychrometricCalculator.vbaRoundUp(
        airflowKgS * reheatingHeatCapacity * (1 + rhSafetyFactor / 100) * 1000,
        0
      );
      reheatingFormula = `${airflowKgS.toFixed(4)} kg/s × ${reheatingHeatCapacity.toFixed(4)} kJ/kg·K × (1 + ${rhSafetyFactor.toFixed(1)} / 100) × 1000 = ${reheatingCapacity.toFixed(0)} W`;
    } else {
      reheatingCapacity = PsychrometricCalculator.vbaRoundUp(
        0.335 * supplyAirflow * rhTempDiff * (1 + rhSafetyFactor / 100),
        0
      );
      reheatingFormula = `0.335 × ${supplyAirflow.toFixed(0)} ㎥/h × ${rhTempDiff.toFixed(1)} ℃ × (1 + ${rhSafetyFactor.toFixed(1)} / 100) = ${reheatingCapacity.toFixed(0)} W`;
    }
  }

  // 가열 코일 (난방)
  let heatingCapacity = deviceHeatingLoad;
  let heatingFormula = '';

  if (deviceHeatingLoad > 0) {
    const heatingSafetyFactor = parseFloat(formData.heatingSafetyFactor) || 10.0;
    const tempDiff = designConditions.heating.indoor.temp - designConditions.heating.outdoor.temp;

    heatingFormula = `0.335 × ${supplyAirflow.toFixed(0)} ㎥/h × ${tempDiff.toFixed(1)} ℃ × (1 + ${heatingSafetyFactor.toFixed(1)} / 100) = ${heatingCapacity.toFixed(0)} W`;
  }

  // 가습기
  let humidifierCapacity = 0;
  let humidifierFormula = '';

  const humidifierType = formData.humidifierType || 'None';
  if (humidifierType !== 'None') {
    const elevation = parseFloat(formData.elevation) || 0;
    const humidifierSafetyFactor = parseFloat(formData.humidifierSafetyFactor) || 10.0;

    const indoorHeatingHR = PsychrometricCalculator.vbaRound(
      PsychrometricCalculator.calculateHumidityRatio(
        designConditions.heating.indoor.temp,
        designConditions.heating.indoor.humidity,
        elevation
      ),
      4
    );

    const outdoorHeatingHR = PsychrometricCalculator.vbaRound(
      PsychrometricCalculator.calculateHumidityRatio(
        designConditions.heating.outdoor.temp,
        designConditions.heating.outdoor.humidity,
        elevation
      ),
      4
    );

    const humidityRatioDiff = indoorHeatingHR - outdoorHeatingHR;

    if (humidityRatioDiff > 0) {
      let calculatedLoad = 1.2 * outdoorAirflow * humidityRatioDiff * (1 + humidifierSafetyFactor / 100);
      humidifierCapacity = PsychrometricCalculator.vbaRoundUp(calculatedLoad, 0);
      // 수식: 실내 습도비 - 외기 습도비
      humidifierFormula = `1.2 × ${outdoorAirflow.toFixed(0)} ㎥/h × (${indoorHeatingHR.toFixed(4)} - ${outdoorHeatingHR.toFixed(4)}) kg/kg × ${(1 + humidifierSafetyFactor / 100).toFixed(2)} = ${humidifierCapacity.toFixed(1)} kg/h`;
    } else {
      // 차이가 0 이하일 때 (가습 불필요)
      humidifierFormula = '';
    }
  }

  return {
    cooling: {
      capacity: coolingCapacity,
      formula: coolingFormula
    },
    preheating: {
      capacity: preheatingCapacity,
      formula: preheatingFormula
    },
    heating: {
      capacity: heatingCapacity,
      formula: heatingFormula
    },
    reheating: {
      capacity: reheatingCapacity,
      formula: reheatingFormula
    },
    humidifier: {
      capacity: humidifierCapacity,
      formula: humidifierFormula
    }
  };
}

/**
 * 습공기 선도 데이터 추출
 */
export function extractPsychrometricChartData(ahu: any): PsychrometricChartData {
  return {
    points: ahu.psychroChartData?.points || [],
    lines: ahu.psychroChartData?.lines || []
  };
}

/**
 * 전체 출력시트 데이터 추출 (메인 함수)
 */
export function extractOutputSheetData(
  ahu: any,
  currentProjectAHUs: any[],
  projectName: string,
  currentProject: any
): OutputSheetData {
  try {
    const projectInfo = extractProjectInfo(projectName, currentProject);
    const systemInfo = extractAirConditioningSystemData(ahu, currentProjectAHUs);
    const designConditions = extractDesignConditions(ahu, currentProjectAHUs);
    const loadPercentages = extractLoadPercentages(ahu, currentProjectAHUs, systemInfo);
    const coilCapacities = extractCoilCapacities(ahu, currentProjectAHUs, systemInfo, designConditions);
    const psychrometricChart = extractPsychrometricChartData(ahu);

    return {
      projectInfo,
      systemInfo,
      designConditions,
      loadPercentages,
      coilCapacities,
      psychrometricChart
    };
  } catch (error) {
    console.error('❌ extractOutputSheetData 오류:', error);
    throw error;
  }
}
